import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import {
  Table,
  message,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Card,
  Descriptions,
  Tag,
  Spin,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { NavLink, useParams } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";

const CALL_STATUSES = [
  "Called",
  "Not Answered",
  "Switched Off",
  "Busy",
  "Wrong Number",
  "Call Back Later",
];

const LEAD_STATUSES = [
  "Interested",
  "Very Interested",
  "Trial Extended",
  "Already Using Another Software",
  "Not Interested",
  "Invalid Lead",
  "Converted",
  "Lost",
];

const leadTagColor = (status) => {
  switch (status) {
    case "Very Interested":
    case "Converted":
      return "green";
    case "Interested":
    case "Trial Extended":
      return "blue";
    case "Call Back Later":
      return "gold";
    case "Not Interested":
    case "Lost":
    case "Invalid Lead":
      return "red";
    case "Already Using Another Software":
      return "orange";
    default:
      return "default";
  }
};

function PumpLeadManagement() {
  const { id } = useParams();
  const [form] = Form.useForm();
  const UserID = localStorage.getItem("AdminID");
  const AccessKey = localStorage.getItem("AdminAccessKey");
  const AddedByName = localStorage.getItem("AdminFull_Name") || "Admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pumpContext, setPumpContext] = useState(null);
  const [history, setHistory] = useState([]);

  const loadData = () => {
    setLoading(true);
    const payload = { UserID, AccessKey, ID: Number(id) };

    Promise.all([
      axios.post(config.base_url + "PumpLead/GetPumpLeadContext", payload),
      axios.post(config.base_url + "PumpLead/GetPumpLeadFollowUps", payload),
    ])
      .then(([contextRes, historyRes]) => {
        if (contextRes.data.status_code === 1) {
          setPumpContext(contextRes.data.PumpContext);
        } else {
          message.error(
            contextRes.data.status_message || "Unable to load pump details."
          );
        }

        if (historyRes.data.status_code === 1) {
          setHistory(historyRes.data.listofRecords || []);
        } else {
          message.error(
            historyRes.data.status_message || "Unable to load lead history."
          );
        }
      })
      .catch(() => message.error("Network error loading lead data."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = "Lead Management — FuelBook ControlBase";
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = (values) => {
    setSaving(true);
    const data = {
      UserID: Number(UserID),
      AccessKey,
      PumpID: Number(id),
      CallDate: values.CallDate
        ? values.CallDate.format("YYYY-MM-DD HH:mm:ss")
        : dayjs().format("YYYY-MM-DD HH:mm:ss"),
      CallStatus: values.CallStatus,
      LeadStatus: values.LeadStatus,
      ClientResponse: values.ClientResponse || "",
      NextFollowUpDate: values.NextFollowUpDate
        ? values.NextFollowUpDate.format("YYYY-MM-DD HH:mm:ss")
        : null,
      ReminderNotes: values.ReminderNotes || "",
      AddedByName,
    };

    axios
      .post(config.base_url + "PumpLead/AddPumpLeadFollowUp", data)
      .then((response) => {
        if (response.data.status_code === 1) {
          message.success(response.data.status_message);
          form.resetFields();
          form.setFieldsValue({ CallDate: dayjs() });
          loadData();
        } else {
          message.error(
            response.data.status_message || "Unable to save follow-up."
          );
        }
      })
      .catch(() => message.error("Network error saving follow-up."))
      .finally(() => setSaving(false));
  };

  const historyColumns = [
    {
      title: "Date",
      dataIndex: "CallDate",
      key: "CallDate",
      width: 120,
      render: (val) => (val ? dayjs(val).format(config.date_format) : "—"),
    },
    {
      title: "Call Status",
      dataIndex: "CallStatus",
      key: "CallStatus",
      width: 130,
    },
    {
      title: "Lead Status",
      dataIndex: "LeadStatus",
      key: "LeadStatus",
      width: 180,
      render: (val) => (val ? <Tag color={leadTagColor(val)}>{val}</Tag> : "—"),
    },
    {
      title: "Client Response",
      dataIndex: "ClientResponse",
      key: "ClientResponse",
      ellipsis: true,
    },
    {
      title: "Next Follow-up",
      dataIndex: "NextFollowUpDate",
      key: "NextFollowUpDate",
      width: 120,
      render: (val) => (val ? dayjs(val).format(config.date_format) : "—"),
    },
    {
      title: "Added By",
      dataIndex: "AddedByName",
      key: "AddedByName",
      width: 120,
    },
    {
      title: "Created At",
      dataIndex: "Created_Date",
      key: "Created_Date",
      width: 140,
      render: (val) =>
        val ? dayjs(val).format("DD MMM YYYY HH:mm") : "—",
    },
  ];

  if (loading && !pumpContext) {
    return (
      <div className="right-side-contents">
        <div
          className="page-content control-base-wrap"
          style={{ textAlign: "center", padding: 80 }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="right-side-contents">
      <div className="page-content control-base-wrap">
        <div className="page-header">
          <h3 className="page-title">
            <NavLink to="/controlbase/petrolpump">
              <ChevronLeftIcon style={{ width: 20, height: 20 }} />
            </NavLink>
            Lead Management
          </h3>
        </div>

        {pumpContext ? (
          <Card size="small" className="mb-4" title={pumpContext.PumpName}>
            <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
              <Descriptions.Item label="Pump Code">
                {pumpContext.PumpCode}
              </Descriptions.Item>
              <Descriptions.Item label="Owner">
                {pumpContext.OwnerName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {pumpContext.Phone || pumpContext.Mobile || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="City / Address">
                {pumpContext.City || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Registration">
                {pumpContext.RegistrationDate
                  ? dayjs(pumpContext.RegistrationDate).format(config.date_format)
                  : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Subscription">
                <Tag
                  color={
                    pumpContext.SubscriptionStatus === "Active" ? "green" : "red"
                  }
                >
                  {pumpContext.SubscriptionStatus}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Trial / Plan Expiry">
                {pumpContext.TrialExpiry
                  ? dayjs(pumpContext.TrialExpiry).format(config.date_format)
                  : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Last Login">
                {pumpContext.LastLogin
                  ? dayjs(pumpContext.LastLogin).format("DD MMM YYYY HH:mm")
                  : "Never"}
              </Descriptions.Item>
              <Descriptions.Item label="Current Plan">
                {pumpContext.CurrentPlan || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Current Lead Status">
                {pumpContext.LatestLeadStatus ? (
                  <Tag color={leadTagColor(pumpContext.LatestLeadStatus)}>
                    {pumpContext.LatestLeadStatus}
                  </Tag>
                ) : (
                  "No contact yet"
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ) : null}

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={9}>
            <Card size="small" title="Add New Follow-up">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ CallDate: dayjs() }}
              >
                <Form.Item
                  name="CallDate"
                  label="Call Date"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <DatePicker showTime style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item
                  name="CallStatus"
                  label="Call Status"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Select
                    placeholder="Select call status"
                    options={CALL_STATUSES.map((s) => ({ value: s, label: s }))}
                  />
                </Form.Item>

                <Form.Item
                  name="LeadStatus"
                  label="Lead Status"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Select
                    placeholder="Select lead status"
                    options={LEAD_STATUSES.map((s) => ({ value: s, label: s }))}
                  />
                </Form.Item>

                <Form.Item name="ClientResponse" label="Client Response">
                  <Input.TextArea rows={3} placeholder="Notes from the call" />
                </Form.Item>

                <Form.Item name="NextFollowUpDate" label="Next Follow-up Date">
                  <DatePicker showTime style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item name="ReminderNotes" label="Reminder Notes">
                  <Input.TextArea rows={2} placeholder="Internal reminder" />
                </Form.Item>

                <Form.Item label="Added By">
                  <Input value={AddedByName} disabled />
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                  shape="round"
                  block
                >
                  Save Follow-up
                </Button>
              </Form>
            </Card>
          </Col>

          <Col xs={24} lg={15}>
            <Card size="small" title="Lead History">
              <Table
                size="small"
                bordered
                loading={loading}
                columns={historyColumns}
                dataSource={history}
                rowKey="ID"
                pagination={{ pageSize: 10, showSizeChanger: true }}
                scroll={{ x: "max-content" }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default PumpLeadManagement;
